import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SeoLayout from "@/components/SeoLayout";
import Jsonld, { SeoMeta } from "@/components/Jsonld";
import BookingWizard from "@/components/BookingWizard";
import { NEIGHBORHOODS } from "@/data/seo";
import { apiPublic } from "@/lib/api";
import { MapPin, Truck, Clock, Star, Sparkles } from "lucide-react";

export default function Location() {
  const { slug } = useParams();
  const hood = NEIGHBORHOODS.find((n) => n.slug === slug);
  const [local, setLocal] = useState(null);

  useEffect(() => {
    if (!hood) return;
    apiPublic.get(`/local/${slug}`).then((r) => setLocal(r.data)).catch(() => setLocal(null));
  }, [slug, hood]);

  if (!hood) return <Navigate to="/" replace />;

  const title = `${hood.name} Appliance Repair · Same-Day in ${hood.zip} | Space Appliance`;
  const description = `Same-day appliance repair in ${hood.name}, San Antonio. Factory-trained on Samsung, LG, Whirlpool. Written quotes. 5.0★ rated on Google. Call (210) 800-1710.`;

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Space Appliance Repair · ${hood.name}`,
    image: "/brand/logo.png",
    telephone: "+1-210-800-1710",
    priceRange: "$$",
    address: { "@type": "PostalAddress", streetAddress: "5504 Bandera Rd", addressLocality: "San Antonio", addressRegion: "TX", postalCode: "78238", addressCountry: "US" },
    areaServed: { "@type": "Place", name: hood.name, geo: { "@type": "GeoCoordinates", latitude: hood.lat, longitude: hood.lng } },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "111" },
  };

  return (
    <SeoLayout>
      <SeoMeta title={title} description={description} />
      <Jsonld id={`ld-location-${slug}`} data={localBusiness} />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-14">
        <div className="chip"><MapPin className="w-3.5 h-3.5" /> {hood.name} · {hood.zip}</div>
        <h1 className="display-retro mt-5 text-cream text-4xl sm:text-6xl normal-case font-semibold leading-[1.05]">
          Appliance repair in <span className="text-orange">{hood.name}</span><span className="text-gold">.</span>
        </h1>
        <p className="mt-4 text-lg text-cream/80 max-w-2xl leading-relaxed">{hood.hook}</p>
        <p className="mt-3 text-cream/70 max-w-2xl">
          Same-day service to {hood.landmarks.join(", ")} and every street in between. Written quotes before any work, factory-trained
          technicians, and a 90-day labor warranty on everything we touch.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <Stat icon={Truck} label="Trucks in your area" value="Daily" />
          <Stat icon={Clock} label="Same-day windows" value="Held until 4 pm" />
          <Stat icon={Star} label="Google rating" value="5.0★ · 111 reviews" />
        </div>
      </section>

      {/* Local stats panel (from backend) */}
      <section className="relative z-10 bg-navy-2/60 border-y border-cream/10 py-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-4">Live · last 90 days</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card label="Jobs nearby" value={local?.jobs_nearby ?? "—"} />
            <Card label="Avg ticket" value={local?.avg_ticket ? `$${Number(local.avg_ticket).toFixed(0)}` : "$185"} />
            <Card label="Most-failed" value={local?.top_appliance || "Refrigerator"} />
            <Card label="Avg response" value={local?.avg_response_minutes ? `${local.avg_response_minutes} min` : "~22 min"} />
          </div>
        </div>
      </section>

      {/* Embedded booking */}
      <section id="book" className="relative z-10 py-20 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Sparkles className="w-3.5 h-3.5" /> Mission Control · {hood.name}</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Book appointment</h2>
            <p className="mt-3 text-cream/70 max-w-xl mx-auto">
              Same-day in {hood.name}. Use code <span className="text-gold font-semibold tracking-widest">GRAND_OPENING</span> for $10 off.
            </p>
          </div>
          <div className="mt-10"><BookingWizard variant="embed" /></div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-10 border-t border-cream/10">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Other neighborhoods</div>
        <div className="flex flex-wrap gap-2">
          {NEIGHBORHOODS.filter((n) => n.slug !== slug).map((n) => (
            <Link key={n.slug} to={`/locations/${n.slug}`} className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors" data-testid={`xlink-loc-${n.slug}`}>
              {n.name}
            </Link>
          ))}
        </div>
      </section>
    </SeoLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-navy-2/60 border border-cream/15 rounded-md p-4 flex items-center gap-3">
      <Icon className="w-5 h-5 text-orange shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-cream/60">{label}</div>
        <div className="text-cream display-retro text-base">{value}</div>
      </div>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-navy/60 border border-cream/15 rounded-md p-4 text-center">
      <div className="text-[10px] uppercase tracking-widest text-cream/60">{label}</div>
      <div className="display-retro text-orange text-2xl mt-1">{value}</div>
    </div>
  );
}
